import { action, observable, runInAction } from 'mobx';
import { IntlShape } from 'react-intl';
import { Container, Inject } from 'typescript-ioc';
import { IModuleVM } from '../../infrastructure/components/module-vm';
import { IReactIntlProvider } from '../../infrastructure/react-intl-provider';
import { Unsubscribe } from 'router5/dist/types/base';
import { RouteParams, Routes } from '../../routes-table';
import { IRouter } from '../../infrastructure/router';
import { alertServerError } from '../global/modal/alerts-host/alert-templates';
import { IErrorParsing } from '../../infrastructure/error-parsing';
import { ISubWindowFacade, ISubWindowsService, SubWindowStatus } from '../../infrastructure/sub-windows';
import { IEmailTemplateField, IEmailTemplatesDAO } from '../../api/email-templates.dao';
import { RADIX_PARAM } from '../../constants/common.constants';
import { EDIT_EMAIL_TEMPLATE, NEW_EMAIL_TEMPLATE } from './email-templates.text';
import { EmailTemplateForm } from './_email-templates-form/email-templates-form';

export class EmailTemplatesVM implements IModuleVM {
    private static handleApiException(e: Error, title: string = 'Error'): void {
        if (e) alertServerError(title, Container.get(IErrorParsing).extractMessage(e));
    }
    private subscriptions: Unsubscribe[];
    @observable
    public isLoading = false;
    @observable
    public templatesList: IEmailTemplateField[] = [];
    @observable
    private emailTemplatesIdToEditPopupMap: { [key: string]: ISubWindowFacade } = {};

    constructor(
        @Inject private readonly router: IRouter<RouteParams> = Container.get(IRouter),
        @Inject private readonly intlProvider: IReactIntlProvider = Container.get(IReactIntlProvider),
        @Inject private readonly subWindowsService: ISubWindowsService = Container.get(ISubWindowsService),
        @Inject private readonly emailTemplatesDAO: IEmailTemplatesDAO = Container.get(IEmailTemplatesDAO),
    ) {

    };
    public get intl(): IntlShape { return this.intlProvider.getIntl(); }

    public didMount = (): void => {
        this.subscriptions = [];
        this.subscriptions.push(
            this.router.listen(Routes.EmailTemplates, async () => {
                this.fetchEmailTemplatesList();
            })
        );
    };

    public willUnmount = (): void => {
        this.subscriptions.forEach(x => x());
    };

    public showCreateTemplateForm = (): void => {
        this.subWindowsService.showSubWindow({
            title: this.intl.formatMessage(
                NEW_EMAIL_TEMPLATE,
                {
                    prefix: ''
                }
            ),
            isExpandedByDefault: true,
            componentToHost: EmailTemplateForm,
            componentToHostProps: {
                accountId: this.getAccountId(),
                saveCompletedCallback: this.fetchEmailTemplatesList
            },
        });
    };

    @action
    public openEditBlueprintForm = (emailTemplateId: string): ISubWindowFacade => {
        const alreadyShownSubWindow = this.emailTemplatesIdToEditPopupMap[emailTemplateId];

        if (alreadyShownSubWindow && alreadyShownSubWindow.getStatus() !== SubWindowStatus.Closed) {
            alreadyShownSubWindow.expand();
            return;
        }
        this.emailTemplatesIdToEditPopupMap[emailTemplateId] = this.subWindowsService.showSubWindow({
            title: this.intl.formatMessage(EDIT_EMAIL_TEMPLATE),
            componentToHost: EmailTemplateForm,
            isExpandedByDefault: true,
            componentToHostProps: {
                accountId: this.getAccountId(),
                saveCompletedCallback: this.fetchEmailTemplatesList,
                deleteCompletedCallback: (id: string) => {
                    this.fetchEmailTemplatesList();
                    this.closeSubWindow(id);
                },
                emailTemplateId: emailTemplateId,
            }
        });
    };

    public isTemplateEditFormShown = (emailTemplateId: string): boolean => {
        return (
            this.emailTemplatesIdToEditPopupMap[emailTemplateId] &&
            this.emailTemplatesIdToEditPopupMap[emailTemplateId].getStatus() !== SubWindowStatus.Closed
        );
    };

    private getAccountId(): number {
        return parseInt(this.routeParams.accountId, RADIX_PARAM);
    }

    private get routeParams(): { accountId: string } {
        return this.router.router.getState().params;
    }

    @action
    private fetchEmailTemplatesList = async (): Promise<void> => {
        try {
            runInAction(() => {
                this.isLoading = true;
            });
            const templatesList = await this.emailTemplatesDAO.getEmailTemplatesList({
                accountId: this.getAccountId(),
            });
            runInAction(() => {
                this.templatesList = templatesList
                    .slice()
                    .sort((a, b) => a.name.localeCompare(b.name));
            });
        } catch (error) {
            EmailTemplatesVM.handleApiException(error);
        } finally {
            runInAction(() => {
                this.isLoading = false;
            });
        }
    };

    private closeSubWindow = (id: string): void => {
        this.emailTemplatesIdToEditPopupMap[id].close();
    };
};
